import { useState, useEffect, useRef, useCallback } from 'react';
import { docTitle, hasOwnTitle, TITLE_SOURCE_LABEL } from '../../domain/documentTitle';
import SegInlineEdit from '../segments/SegInlineEdit';
import { api } from '../../api';
import { useApp } from '../../contexts/AppContext';
import { useInventory } from '../../contexts/InventoryContext';
import {
  formatDate, formatSize, getSummary, getDocStats, uploadedAt,
  getDocTypeDisplay, getCanonicalType, getTypeIcon,
  formatDuration, getMediaDuration,
} from '../../hooks/useInventoryData';
import ClassificationEditor from './ClassificationEditor';
import { isCutChild } from '../segments/cutChild';
import DocumentPreview from '../preview/DocumentPreview';
import UpgradeTranscriptModal from '../UpgradeTranscriptModal';
import ChecklistImportModal from './ChecklistImportModal';
import SpeakerLabelPopover from '../SpeakerLabelPopover';
import ImproveTranscriptCard from '../ImproveTranscriptCard';
import useTranscriptUpgrade from '../../hooks/useTranscriptUpgrade';
import DetectedDocumentsSection from './DetectedDocumentsSection';

function fmtTime(s) {
  if (s == null) return '';
  // Hours once a position passes an hour (hearings run for hours).
  const total = Math.floor(s);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = total % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/**
 * DocumentDetailPanel — inline expanded detail for a document row.
 *
 * Shows metadata grid, action buttons (view, move, delete, details),
 * and summary text. Self-contained — manages its own action states.
 *
 * Props:
 *   doc           — document object
 *   caseId        — UUID
 *   folderPaths   — array of all folder paths (for move dropdown)
 *   onDocChange   — callback() when doc is mutated (moved/deleted)
 *   onShowDetail  — callback(docId) to open the full detail modal
 */
function CoverageBar({ pct }) {
  if (pct == null) return null;
  return (
    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 4, background: '#1a2a3a', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.min(pct, 100)}%`,
          height: '100%',
          background: pct >= 100 ? '#4caf50' : '#2196f3',
          borderRadius: 2,
        }} />
      </div>
      <span style={{ fontSize: 11, color: '#8899aa', whiteSpace: 'nowrap' }}>{Math.round(pct)}%</span>
    </div>
  );
}

// Group consecutive whisper word-segments into ~10-word clickable lines.
function groupWhisperLines(whisperSegments) {
  const lines = [];
  let words = [];
  let lineStart = null;
  whisperSegments.forEach((seg, i) => {
    if (lineStart === null) lineStart = seg.start;
    words.push(seg.text);
    if (words.length >= 10 || i === whisperSegments.length - 1) {
      lines.push({ start: lineStart, text: words.join(' ') });
      words = [];
      lineStart = null;
    }
  });
  return lines;
}

function SegmentPanel({ title, subtitle, lines, onSeek, panelKey, syncedTime, syncSourceRef, onSyncScroll }) {
  const containerRef = useRef(null);
  const isProgrammaticRef = useRef(false);

  // Apply incoming syncedTime — scroll to the line whose start <=
  // syncedTime, but only when another panel was the source. Mark the
  // resulting scroll as programmatic so our scroll listener ignores it.
  useEffect(() => {
    if (!containerRef.current || !lines || lines.length === 0) return;
    if (syncSourceRef?.current === panelKey) return;

    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].start <= syncedTime) idx = i;
      else break;
    }
    if (idx < 0) idx = 0;

    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${idx}"]`);
    if (!el) return;

    isProgrammaticRef.current = true;
    const cRect = c.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    c.scrollTop += elRect.top - cRect.top;
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 80);
    return () => clearTimeout(tid);
  }, [syncedTime, lines, panelKey, syncSourceRef]);

  // Detect user scrolls — find the segment at the top of the viewport
  // and report its timestamp upward.
  useEffect(() => {
    const c = containerRef.current;
    if (!c || !lines || lines.length === 0 || !onSyncScroll) return;
    const handler = () => {
      if (isProgrammaticRef.current) return;
      const cTop = c.getBoundingClientRect().top;
      let topIdx = 0;
      const segs = c.querySelectorAll('[data-idx]');
      for (let i = 0; i < segs.length; i++) {
        if (segs[i].getBoundingClientRect().bottom >= cTop + 4) {
          topIdx = parseInt(segs[i].dataset.idx, 10);
          break;
        }
      }
      const t = lines[topIdx]?.start;
      if (t == null) return;
      onSyncScroll(panelKey, t);
    };
    c.addEventListener('scroll', handler, { passive: true });
    return () => c.removeEventListener('scroll', handler);
  }, [lines, panelKey, onSyncScroll]);

  if (!lines || lines.length === 0) return null;
  return (
    <div className="inv-transcript-panel">
      <div className="inv-transcript-panel-header">
        <span>{title}</span>
        {subtitle && <span className="inv-transcript-panel-subtitle">{subtitle}</span>}
      </div>
      <div className="inv-transcript-segments" ref={containerRef}>
        {lines.map((seg, i) => (
          <div key={i} data-idx={i} className="inv-transcript-seg" onClick={() => onSeek?.(seg.start)}
               title={`Open at ${fmtTime(seg.start)}`}>
            <span className="inv-transcript-seg-ts">{fmtTime(seg.start)}</span>
            {seg.source === 'video' && <span className="inv-transcript-seg-src" title="Written by the video pass (Gemini), not by speech-to-text">video</span>}
            <span className="inv-transcript-seg-txt">{seg.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Canonical chat-log view of the upgrade-tier transcript. Speaker
// label appears once per turn; consecutive lines from the same speaker
// stay grouped. Click any line to open the viewer at that timestamp.
function MergedTranscriptPanel({
  lines, onSeek, panelKey, syncedTime, syncSourceRef, onSyncScroll, providerLabel,
  speakerLabels, personEntities, docId, onLabelsUpdated,
}) {
  const containerRef = useRef(null);
  const isProgrammaticRef = useRef(false);
  // Single popover at panel level, anchored to whichever speaker
  // header was clicked. Multiple turns share the same letter, so
  // anchoring per-turn would render duplicates.
  const [openLetter, setOpenLetter] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  function closePopover() {
    setOpenLetter(null);
    setAnchorEl(null);
  }

  function setSpeakerName(letter, name) {
    const prior = { ...(speakerLabels || {}) };
    const next = { ...prior };
    if (name === '') delete next[letter];
    else next[letter] = name;
    // Optimistic update: paint the new name immediately, revert on
    // failure. Without this, network latency + the parent's
    // refetch-on-data-change can race and visually swallow the rename.
    onLabelsUpdated?.(next);
    closePopover();
    api.patch(`/documents/${docId}/speaker-labels`, { labels: next })
      .catch(err => {
        console.error('speaker-labels PATCH failed', err);
        onLabelsUpdated?.(prior);
      });
  }

  // Programmatic scroll on incoming sync.
  useEffect(() => {
    if (!containerRef.current || !lines || lines.length === 0) return;
    if (syncSourceRef?.current === panelKey) return;
    let idx = -1;
    for (let i = 0; i < lines.length; i++) {
      if ((lines[i].start ?? 0) <= syncedTime) idx = i;
      else break;
    }
    if (idx < 0) idx = 0;
    const c = containerRef.current;
    const el = c.querySelector(`[data-idx="${idx}"]`);
    if (!el) return;
    isProgrammaticRef.current = true;
    const cRect = c.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    c.scrollTop += elRect.top - cRect.top;
    const tid = setTimeout(() => { isProgrammaticRef.current = false; }, 80);
    return () => clearTimeout(tid);
  }, [syncedTime, lines, panelKey, syncSourceRef]);

  // User-scroll → emit timestamp upward.
  useEffect(() => {
    const c = containerRef.current;
    if (!c || !lines || lines.length === 0 || !onSyncScroll) return;
    const handler = () => {
      if (isProgrammaticRef.current) return;
      const cTop = c.getBoundingClientRect().top;
      let topIdx = 0;
      const segs = c.querySelectorAll('[data-idx]');
      for (let i = 0; i < segs.length; i++) {
        if (segs[i].getBoundingClientRect().bottom >= cTop + 4) {
          topIdx = parseInt(segs[i].dataset.idx, 10);
          break;
        }
      }
      const t = lines[topIdx]?.start;
      if (t == null) return;
      onSyncScroll(panelKey, t);
    };
    c.addEventListener('scroll', handler, { passive: true });
    return () => c.removeEventListener('scroll', handler);
  }, [lines, panelKey, onSyncScroll]);

  if (!lines || lines.length === 0) return null;

  return (
    <div className="inv-transcript-panel inv-transcript-panel--merged">
      <div className="inv-transcript-panel-header">
        <span>✨ Full Transcript</span>
        <span className="inv-transcript-panel-subtitle">
          {providerLabel ? `via ${providerLabel}` : ''}
        </span>
      </div>
      <div className="inv-transcript-segments inv-merged-segments" ref={containerRef}>
        {lines.map((seg, i) => {
          const prevSpeaker = i > 0 ? lines[i - 1].speaker : null;
          const speakerChanged = seg.speaker !== prevSpeaker;
          const displayName = seg.speaker
            ? (speakerLabels?.[seg.speaker] || `Speaker ${seg.speaker}`)
            : null;
          return (
            <div
              key={i}
              data-idx={i}
              className={`inv-merged-line ${speakerChanged ? 'inv-merged-line--turn' : ''}`}
              title={`Open at ${fmtTime(seg.start)}`}
            >
              {speakerChanged && seg.speaker && docId && (
                <span
                  className="inv-merged-speaker"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (openLetter === seg.speaker) closePopover();
                    else { setOpenLetter(seg.speaker); setAnchorEl(e.currentTarget); }
                  }}
                  title="Click to rename this speaker"
                >
                  {displayName}
                </span>
              )}
              <div
                className="inv-merged-row"
                onClick={() => onSeek?.(seg.start)}
                style={{ cursor: 'pointer' }}
              >
                <span className="inv-transcript-seg-ts">{fmtTime(seg.start)}</span>
                <span className="inv-merged-text">{seg.text}</span>
              </div>
            </div>
          );
        })}
      </div>
      {openLetter && anchorEl && (
        <SpeakerLabelPopover
          letter={openLetter}
          currentName={speakerLabels?.[openLetter] || null}
          personEntities={personEntities}
          anchorEl={anchorEl}
          onSet={(name) => setSpeakerName(openLetter, name)}
          onClose={closePopover}
        />
      )}
    </div>
  );
}

function PanelToggle({ active, onToggle, icon, label, count }) {
  return (
    <button
      className={`inv-transcript-toggle ${active ? 'inv-transcript-toggle--active' : ''}`}
      onClick={onToggle}
      type="button"
    >
      <span>{icon} {label}</span>
      {count != null && <span className="inv-transcript-toggle-count">{count}</span>}
    </button>
  );
}

// Click-to-seek transcript renderer. Stacks every available source so
// the user can compare Cloud Speech segments vs Whisper word-level vs
// Gemini visual timeline. Clicks bubble up via onSeek(timestamp) so
// the parent opens the full-screen viewer at that point.
function TranscriptBody({
  data, onSeek, onCompleteFull, transcribing, transcribeMsg, isPartial,
  isMedia, onRequestUpgrade, docId, tier,
}) {
  const merged = data?.merged_transcript || [];
  const whisperSegments = data?.whisper_segments || [];
  const segments = data?.transcript_segments || [];
  const frameDescs = data?.frame_descriptions || [];
  const transcription = data?.transcription || '';
  const coverage = data?.extraction_coverage;
  const upgradeJob = data?.extraction?.upgrade_job || data?.upgrade_job || null;
  const upgradeJobStatus = upgradeJob?.status || null;
  const mediaIntel = data?.media_intelligence || null;
  const personEntities = (mediaIntel?.entities || [])
    .filter(e => (e?.entity_type || '').toLowerCase() === 'person_name')
    .map(e => e.text);
  // Speaker-label mapping persists per doc; the panel may also keep
  // an optimistic local override that hasn't refetched yet.
  const [speakerLabels, setSpeakerLabels] = useState(data?.speaker_labels || {});
  useEffect(() => { setSpeakerLabels(data?.speaker_labels || {}); }, [data]);

  const hasMerged = merged.length > 0;
  const hasWhisper = whisperSegments.length > 0;
  // Where the speech lines came from: Cloud Speech samples, the native video
  // pass, or both — and what the guard left out of the video's transcript.
  const speechFromVideo = segments.filter((seg) => seg.source === 'video').length;
  const speechFromSpeech = segments.length - speechFromVideo;
  const speechSubtitle = speechFromVideo && speechFromSpeech
    ? `${speechFromSpeech} from speech-to-text · ${speechFromVideo} from the video`
    : speechFromVideo ? 'from the video (Gemini)' : 'speech-to-text';
  const leftOut = data?.transcript_left_out;
  const hasSegments = segments.length > 0;
  const hasFrameDescs = frameDescs.length > 0;
  const hasAny = hasMerged || hasWhisper || hasSegments || hasFrameDescs || !!transcription;
  const whisperLines = hasWhisper ? groupWhisperLines(whisperSegments) : [];

  // Per-panel visibility. When merged exists, default to merged-only
  // (it's the canonical view); the source panels become opt-in.
  const [showMerged, setShowMerged] = useState(true);
  const [showSpeech, setShowSpeech] = useState(!hasMerged);
  const [showWhisperPanel, setShowWhisperPanel] = useState(!hasMerged);
  const [showFrames, setShowFrames] = useState(!hasMerged);
  const [showSourceToggles, setShowSourceToggles] = useState(false);

  const visibleCount = (hasMerged && showMerged ? 1 : 0)
    + (hasSegments && showSpeech ? 1 : 0)
    + (hasWhisper && showWhisperPanel ? 1 : 0)
    + (hasFrameDescs && showFrames ? 1 : 0);

  // Scroll-sync — when one panel scrolls, the others jump to the
  // line whose timestamp matches the top of the source panel.
  // syncSourceRef + a 250ms clear timer break the feedback loop:
  // while a panel keeps scrolling, it stays the source; the others
  // react but their programmatic-scroll flag suppresses re-emission.
  const [syncedTime, setSyncedTime] = useState(0);
  const syncSourceRef = useRef(null);
  const clearSourceTimerRef = useRef(null);
  const onSyncScroll = useCallback((panelKey, t) => {
    syncSourceRef.current = panelKey;
    if (clearSourceTimerRef.current) clearTimeout(clearSourceTimerRef.current);
    clearSourceTimerRef.current = setTimeout(() => {
      syncSourceRef.current = null;
    }, 250);
    setSyncedTime(t);
  }, []);
  useEffect(() => () => {
    if (clearSourceTimerRef.current) clearTimeout(clearSourceTimerRef.current);
  }, []);

  return (
    <>
      <CoverageBar pct={coverage} />
      {!hasAny && <span className="text-muted">No transcript available.</span>}

      {/* Source visibility toggles — primary "Full Transcript" plus
          opt-in source panels behind a disclosure. */}
      {(hasMerged || hasSegments || hasWhisper || hasFrameDescs) && (
        <div className="inv-transcript-toggles">
          {hasMerged && (
            <PanelToggle
              active={showMerged}
              onToggle={() => setShowMerged(!showMerged)}
              icon="✨" label="Full Transcript"
              count={merged.length}
            />
          )}
          {(hasSegments || hasWhisper || hasFrameDescs) && (
            <button
              className="inv-transcript-toggle"
              onClick={() => setShowSourceToggles(!showSourceToggles)}
              type="button"
              title="Show / hide the source transcripts"
            >
              <span>{showSourceToggles ? '▾' : '▸'} Sources</span>
            </button>
          )}
          {showSourceToggles && hasSegments && (
            <PanelToggle
              active={showSpeech}
              onToggle={() => setShowSpeech(!showSpeech)}
              icon="📝" label="Speech"
              count={segments.length}
            />
          )}
          {showSourceToggles && hasWhisper && (
            <PanelToggle
              active={showWhisperPanel}
              onToggle={() => setShowWhisperPanel(!showWhisperPanel)}
              icon="🎙" label="Whisper"
              count={`${whisperSegments.length} words`}
            />
          )}
          {showSourceToggles && hasFrameDescs && (
            <PanelToggle
              active={showFrames}
              onToggle={() => setShowFrames(!showFrames)}
              icon="🎥" label="Visual"
              count={frameDescs.length}
            />
          )}
        </div>
      )}

      {leftOut?.count > 0 && (
        <div className="inv-transcript-leftout" data-testid="transcript-left-out">
          {leftOut.count} {leftOut.count === 1 ? 'line' : 'lines'} of the video's transcript {leftOut.count === 1 ? 'was' : 'were'} left out as unusable
          ({(leftOut.reasons || []).map((r) => ({ repetition: 'repeated phrase', placeholder: 'placeholder for silence', truncated: 'cut off' })[r] || r).join(', ')}).
        </div>
      )}
      {/* Side-by-side panels — only render those toggled on */}
      {visibleCount > 0 && (
        <div className="inv-transcript-panels-row">
          {hasMerged && showMerged && (
            <MergedTranscriptPanel
              lines={merged}
              onSeek={onSeek}
              panelKey="merged" syncedTime={syncedTime}
              syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
              providerLabel={mediaIntel?.provider_label || mediaIntel?.provider}
              speakerLabels={speakerLabels}
              personEntities={personEntities}
              docId={docId}
              onLabelsUpdated={(next) => setSpeakerLabels(next)}
            />
          )}
          {hasSegments && showSpeech && (
            <SegmentPanel
              title="Speech Transcript" subtitle={speechSubtitle} lines={segments} onSeek={onSeek}
              panelKey="speech" syncedTime={syncedTime}
              syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
            />
          )}
          {hasWhisper && showWhisperPanel && (
            <SegmentPanel
              title="Whisper Transcript"
              subtitle={`word-for-word · ${whisperSegments.length} words`}
              lines={whisperLines} onSeek={onSeek}
              panelKey="whisper" syncedTime={syncedTime}
              syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
            />
          )}
          {hasFrameDescs && showFrames && (
            <SegmentPanel
              title="Visual Timeline" lines={frameDescs} onSeek={onSeek}
              panelKey="frames" syncedTime={syncedTime}
              syncSourceRef={syncSourceRef} onSyncScroll={onSyncScroll}
            />
          )}
        </div>
      )}
      {visibleCount === 0 && hasAny && (
        <div className="text-muted" style={{ padding: '8px 0', fontSize: 12 }}>
          Use the toggles above to show a transcript source.
        </div>
      )}
      {!hasMerged && !hasSegments && !hasWhisper && !hasFrameDescs && transcription && (
        <pre>{transcription}</pre>
      )}

      {/* Improve transcript — two-option card replacing the prior
          two stacked buttons. Each path knows what it costs and
          whether audio leaves Workloads. Both buttons disable while
          either upgrade is queued/processing so the user can't
          accidentally double-bill. */}
      {/* Redacted copies are never indexed — no transcript-upgrade affordance. */}
      {tier !== 'excluded' && (
        <ImproveTranscriptCard
          isMedia={isMedia}
          extractionCoverage={coverage}
          hasMerged={hasMerged}
          tier={tier}
          upgradeJobStatus={upgradeJobStatus}
          fullTranscribing={transcribing}
          transcribeMsg={transcribeMsg}
          onRunFullCoverage={onCompleteFull}
          onGenerateAITranscript={onRequestUpgrade}
        />
      )}
      {upgradeJobStatus === 'failed' && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#ff8a8a' }}>
          Last AI transcript failed: {upgradeJob?.error || 'unknown error'}
        </div>
      )}
    </>
  );
}

function EntitiesSection({ doc, show, onToggle }) {
  const entities = doc.analysis?.entities || {};
  const keyEntities = doc.analysis_result?.key_entities || [];
  const entityTypes = Object.entries(entities).filter(([, items]) => items?.length > 0);
  const hasAny = entityTypes.length > 0 || keyEntities.length > 0;
  if (!hasAny) return null;

  return (
    <div className="inv-detail-entities">
      <button className="btn btn-sm btn-ghost" onClick={onToggle}>
        {show ? '▾ Hide Entities' : '▸ Show Entities'}
        <span style={{ marginLeft: 8, fontSize: 11, color: '#8899aa' }}>
          {keyEntities.length > 0 && `${keyEntities.length} key`}
          {keyEntities.length > 0 && entityTypes.length > 0 && ' · '}
          {entityTypes.length > 0 && `${entityTypes.length} type${entityTypes.length === 1 ? '' : 's'}`}
        </span>
      </button>
      {show && (
        <div className="inv-entities-content">
          {keyEntities.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div className="inv-detail-label" style={{ marginBottom: 4 }}>Key Entities</div>
              <div className="inv-entity-tags">
                {keyEntities.map((e, i) => <span key={i} className="inv-entity-tag">{e}</span>)}
              </div>
            </div>
          )}
          {entityTypes.length > 0 && (
            <div>
              <div className="inv-detail-label" style={{ marginBottom: 4 }}>Entities</div>
              {entityTypes.map(([type, items]) => (
                <div key={type} className="inv-entity-row">
                  <span className="inv-entity-type">{type}</span>
                  <span className="inv-entity-list">{items.join(', ')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TextExcerptSection({ doc, show, onToggle, loaded }) {
  // The /view/text endpoint may return a different field name across
  // doc types; fall back to the value already on the doc.
  const text = loaded?.extracted_text || doc.extracted_text || '';
  const hasText = text.length > 0;
  return (
    <div className="inv-detail-text-excerpt">
      <button className="btn btn-sm btn-ghost" onClick={onToggle}>
        {show ? '▾ Hide Text Excerpt' : '▸ Show Text Excerpt'}
      </button>
      {show && (
        <div className="inv-text-excerpt-content">
          {loaded === null && <span className="text-muted">Loading…</span>}
          {loaded !== null && !hasText && <span className="text-muted">No text extracted.</span>}
          {hasText && (
            <pre>{text.slice(0, 3000)}{text.length > 3000 ? '\n…' : ''}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewSection({ doc, docId, canonicalType }) {
  const [expanded, setExpanded] = useState(false);

  // Audio files have nothing visual to preview.
  if (canonicalType === 'audio') return null;

  const isMedia = canonicalType === 'video';
  const previewLabel = isMedia ? 'Keyframes'
    : canonicalType === 'pdf' ? 'Pages'
    : 'Preview';

  return (
    <div className="inv-detail-filmstrip">
      <button
        className="btn btn-sm btn-ghost"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? `▾ Hide ${previewLabel}` : `▸ Show ${previewLabel}`}
      </button>
      {expanded && (
        <div className="inv-filmstrip-content">
          <DocumentPreview doc={doc} documentId={docId} />
        </div>
      )}
    </div>
  );
}

export default function DocumentDetailPanel({
  doc: docProp,
  caseId,
  folderPaths = [],
  onDocChange,
  onShowDetail,
  onShowViewer,
}) {
  const { dispatch: appDispatch } = useApp();
  const { dispatch: invDispatch } = useInventory();
  // Local doc copy so optimistic mutations from ClassificationEditor (and
  // any future inline editors) take effect immediately without waiting
  // for the parent's list refetch — same pattern DocumentDetail used.
  const [docLocal, setDocLocal] = useState(docProp);
  useEffect(() => { setDocLocal(docProp); }, [docProp]);
  const doc = docLocal;
  const [moveOpen, setMoveOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameBusy, setRenameBusy] = useState(false);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [moveDest, setMoveDest] = useState(doc.folder_path || '__unfiled__');
  const [newFolderPath, setNewFolderPath] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyDest, setCopyDest] = useState('__new__');
  const [copyNewFolderPath, setCopyNewFolderPath] = useState('');
  const [showClassification, setShowClassification] = useState(false);

  const isChecklist = (doc.document_type || '').toLowerCase() === 'discovery_checklist'
    || (doc.document_purpose || '').toLowerCase() === 'discovery_checklist';

  // Document Readiness upgrade state. Local override mirrors the API
  // result so the badge flips immediately without waiting for a list
  // refetch (which is best-effort and doesn't always invalidate the
  // cached `doc` reference passed in via props).
  const [tierOverride, setTierOverride] = useState(null);
  const tier = tierOverride || doc.tier || 'skim';
  const [upgrading, setUpgrading] = useState(null);   // 'surface' | 'deep' | null
  const [upgradeMsg, setUpgradeMsg] = useState(null);
  // Synchronous guard — two clicks in the same React tick both read the
  // same `upgrading` closure, so the state-based guard alone misses
  // rapid double-clicks before the disabled prop commits.
  const upgradeInFlightRef = useRef(false);

  // Per-doc upgrade-cost estimate. Hardcoded prices in the buttons lie
  // ($0.06 for a 1-pager and a 200-pager were both showing). Hit the
  // server's tier-breakdown for this single doc on mount, refresh when
  // the tier flips so a just-upgraded doc no longer shows a price.
  const [upgradeCosts, setUpgradeCosts] = useState(null);  // {surface, deep}
  useEffect(() => {
    if (tier === 'deep') { setUpgradeCosts(null); return; }
    let cancelled = false;
    api.post(`/exploration/case/${caseId}/tier-breakdown`, {
      document_ids: [doc.id],
      complete_extraction: false,
    })
      .then(d => {
        if (cancelled) return;
        setUpgradeCosts({
          surface: Number(d?.surface_upgrade_cost_usd ?? 0),
          deep: Number(d?.deep_upgrade_cost_usd ?? 0),
        });
      })
      .catch(() => { if (!cancelled) setUpgradeCosts(null); });
    return () => { cancelled = true; };
  }, [caseId, doc.id, tier]);

  // Self-heal pending derive-class enrichment stages on doc open.
  // (e.g. videos extracted before keyframe-thumbnails shipped get
  // their thumbnails captured and appended to extraction.preview_images
  // automatically.) Free.
  //
  // The server runs derive stages as a fire-and-forget background task
  // so the panel render is never blocked by FFmpeg / GCS work. When
  // dispatched=true, schedule a few delayed refreshes — by the time the
  // user has read the panel, the thumbnails have appeared.
  useEffect(() => {
    let cancelled = false;
    const timers = [];
    api.get(`/documents/${doc.id}/stages?run=derive`)
      .then(d => {
        if (cancelled || !d?.dispatched) return;
        // Stagger refreshes: 5s catches small videos, 15s catches
        // larger ones. Stops as soon as onDocChange is unmounted.
        for (const ms of [5000, 15000, 30000]) {
          timers.push(setTimeout(() => {
            if (!cancelled) onDocChange?.();
          }, ms));
        }
      })
      .catch(() => { /* silent — never break the panel render */ });
    return () => {
      cancelled = true;
      for (const t of timers) clearTimeout(t);
    };
  }, [doc.id, onDocChange]);

  function fmtCost(usd) {
    if (usd == null) return '…';
    if (usd <= 0) return 'free';
    if (usd < 0.01) return `$${usd.toFixed(4)}`;
    return `$${usd.toFixed(2)}`;
  }

  async function handleUpgrade(targetTier) {
    if (upgradeInFlightRef.current) return;
    upgradeInFlightRef.current = true;
    setUpgrading(targetTier);
    setUpgradeMsg(null);
    try {
      const res = await api.post(`/exploration/case/${caseId}/upgrade-tier`, {
        document_ids: [doc.id],
        target_tier: targetTier,
        approved: true,
      });
      // Trust the server's tier_after when present (inline path); for
      // background-job dispatches the tier flips later via the
      // BackgroundJobsBanner-driven inventory refresh.
      const finalTier = res?.tier_after || (res?.mode === 'inline' ? targetTier : null);
      if (finalTier) setTierOverride(finalTier);
      const isBackground = res?.mode === 'background';
      const pages = res?.completed?.[0]?.pages_extracted ?? 0;
      if (isBackground) {
        setUpgradeMsg({
          text: targetTier === 'deep'
            ? 'Deep search queued — running in the background. The badge will update when it completes.'
            : 'Surface search queued — running in the background.',
          error: false,
        });
      } else {
        setUpgradeMsg({
          text: targetTier === 'deep'
            ? (pages > 0
                ? `Deep search ready — ${pages} new page${pages === 1 ? '' : 's'} extracted.`
                : 'Deep search ready.')
            : 'Surface search ready.',
          error: false,
        });
      }
      onDocChange?.();
      if (isBackground) {
        // Leave `upgrading` and the ref set so the button stays disabled
        // (labeled "Upgrading…") until the parent's onDocChange chain
        // refreshes doc.tier — at which point the whole button block is
        // replaced by the "✓ Deep search" badge. Resetting here would
        // re-enable the button while the bg extraction is still running.
        return;
      }
      setUpgrading(null);
      upgradeInFlightRef.current = false;
    } catch (err) {
      setUpgradeMsg({ text: `Upgrade failed: ${err.message}`, error: true });
      setUpgrading(null);
      upgradeInFlightRef.current = false;
    }
  }

  function handleChecklistImported() {
    // The commit now runs as a background job — progress shows in the
    // BackgroundJobsBanner and a "View checklist" toast fires on completion.
    setStatus({ text: 'Importing checklist… we’ll let you know when it’s ready.', error: false });
    onDocChange?.();
  }

  const dtDisplay = getDocTypeDisplay(doc);
  const ct = getCanonicalType(doc);
  // a cut file's own summary was written before it was cut, from a sample of its pages:
  // its documents each have their own, and the list of them is right here in the pane
  const summary = doc.is_compilation_container ? '' : getSummary(doc);
  const ft = (doc.file_type || '').toLowerCase();
  const isMedia = ct === 'audio' || ct === 'video'
    || ['mp4','webm','mov','avi','mkv','wmv','flv','3gp'].includes(ft)
    || ['mp3','wav','m4a','ogg','flac'].includes(ft);
  const [transcriptData, setTranscriptData] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showTextExcerpt, setShowTextExcerpt] = useState(false);
  const [showEntities, setShowEntities] = useState(false);
  const [requestingWhisper, setRequestingWhisper] = useState(false);
  const [whisperMsg, setWhisperMsg] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  // Google full-coverage upgrade flow (state + window.confirm + bg-job poll)
  // lives in a shared hook so the viewer uses the same code path.
  const upgrade = useTranscriptUpgrade({
    docId: doc.id,
    caseId,
    onTranscriptInvalidated: () => setTranscriptData(null),
    onDocChange,
  });

  // Lazy-load /view/text — covers both media (transcript / segments /
  // frame_descriptions) and non-media (extracted_text). Fired whenever
  // a section that needs it is expanded.
  const needsViewText =
    (isMedia && showTranscript) || (!isMedia && showTextExcerpt);
  useEffect(() => {
    if (!needsViewText || transcriptData !== null) return;
    let cancelled = false;
    api.get(`/documents/${doc.id}/view/text`)
      .then(d => { if (!cancelled) setTranscriptData(d || {}); })
      .catch(() => { if (!cancelled) setTranscriptData({}); });
    return () => { cancelled = true; };
  }, [needsViewText, transcriptData, doc.id]);

  // Auto-refresh while an upgrade-tier transcription is running. The
  // background job updates extraction.upgrade_job.status; we poll
  // /view/text every 10s and stop the moment we see the merged
  // transcript or a non-processing status. This is what makes the
  // chat-log appear without a manual reload after the user clicks
  // Generate and walks away.
  const upgradeJobStatus = transcriptData?.extraction?.upgrade_job?.status
    || transcriptData?.upgrade_job?.status
    || null;
  const upgradeInFlight = upgradeJobStatus === 'processing' || upgradeJobStatus === 'queued';
  useEffect(() => {
    if (!upgradeInFlight) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const d = await api.get(`/documents/${doc.id}/view/text`);
        if (!cancelled) setTranscriptData(d || {});
      } catch { /* swallow — keep polling */ }
    };
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [upgradeInFlight, doc.id]);

  const isPartialTranscript = transcriptData?.extraction_coverage != null
    && transcriptData.extraction_coverage < 100;

  async function handleRequestWhisper() {
    setRequestingWhisper(true);
    setWhisperMsg(null);
    try {
      const est = await api.post(`/processing/documents/${doc.id}/transcribe-whisper?estimate=true`);
      if (est.whisper_status === 'completed') {
        setWhisperMsg('Whisper transcript already exists. Reload to view it.');
        setRequestingWhisper(false);
        return;
      }
      const dur = est.duration_seconds || 0;
      const costCents = est.estimated_cost_cents || 0;
      const ok = window.confirm(
        `Request high-accuracy Whisper transcript?\n` +
        `Duration: ${Math.round(dur)}s (~${Math.round(dur / 60)} min)\n` +
        `Estimated cost: $${(costCents / 100).toFixed(2)}\n\n` +
        `⚠️ DATA BOUNDARY WARNING\n` +
        `This will send the audio file to OpenAI (Whisper API), which is outside\n` +
        `the Google Assured Workloads environment. Audio data will leave Google\n` +
        `Cloud and be processed by a third-party service.\n\n` +
        `Only proceed if this file is approved for processing outside Google Assured Workloads.\n\n` +
        `This may take several minutes for long recordings.`
      );
      if (!ok) { setRequestingWhisper(false); return; }
      const result = await api.post(`/processing/documents/${doc.id}/transcribe-whisper?estimate=false`);
      if (result.success) {
        setWhisperMsg('Whisper transcription complete. Reloading…');
        setTranscriptData(null); // force refetch on next expand
      } else {
        setWhisperMsg(`Failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      setWhisperMsg(`Error: ${err.message}`);
    } finally {
      setRequestingWhisper(false);
    }
  }

  function handleView() {
    // Open the dedicated content viewer
    onShowViewer?.(doc.id);
  }

  async function handleMove() {
    let dest = moveDest === '__new__' ? newFolderPath.trim() : moveDest;
    if (dest === '__unfiled__') dest = null;
    if (moveDest === '__new__' && !dest) return;
    if (dest && !dest.startsWith('/')) dest = '/' + dest;
    try {
      await api.patch(`/documents/${doc.id}/folder`, { folder_path: dest });
      setStatus({ text: 'Moved to ' + (dest || 'Unfiled'), error: false });
      setMoveOpen(false);
      onDocChange?.();
    } catch (err) {
      setStatus({ text: 'Move failed: ' + err.message, error: true });
    }
  }

  async function handleCopy() {
    let dest = copyDest === '__new__' ? copyNewFolderPath.trim() : copyDest;
    if (!dest) return;
    if (!dest.startsWith('/')) dest = '/' + dest;
    try {
      await api.post(`/documents/${doc.id}/folder-copies`, { folder_path: dest });
      setStatus({ text: 'Copied into ' + dest, error: false });
      setCopyOpen(false);
      setCopyNewFolderPath('');
      onDocChange?.();
    } catch (err) {
      setStatus({ text: 'Copy failed: ' + err.message, error: true });
    }
  }

  async function handleRemoveCopy(path) {
    try {
      await api.del(`/documents/${doc.id}/folder-copies`, { folder_path: path });
      setStatus({ text: 'Removed from ' + path, error: false });
      onDocChange?.();
    } catch (err) {
      setStatus({ text: 'Remove failed: ' + err.message, error: true });
    }
  }

  async function handleDelete() {
    try {
      await api.del(`/documents/${doc.id}`);
      // Optimistic update — remove from list immediately without waiting for refresh.
      appDispatch({ type: 'REMOVE_DOCUMENT', id: doc.id });
      invDispatch({ type: 'TOGGLE_DOC_EXPAND', id: doc.id }); // collapse so it doesn't linger
      onDocChange?.(); // background refresh for eventual consistency
    } catch (err) {
      setStatus({ text: 'Delete failed: ' + err.message, error: true });
    }
  }

  const excerptRec = doc?.characterization?.excerpt || null;
  const excerptStart = Number(excerptRec?.time_start);
  const excerptEnd = Number(excerptRec?.time_end);

  return (
    <div className="inv-detail-panel">
      {/* Derived provenance — an excerpt says where it was cut from and opens
          the original at that point; a redacted copy says what it is. */}
      {doc.derived_kind === 'excerpt' && (
        <div className="inv-detail-derived" data-testid="excerpt-provenance">
          <span>
            ✂ Excerpt cut from <strong>{excerptRec?.source_filename || 'a longer recording'}</strong>
            {Number.isFinite(excerptStart) && Number.isFinite(excerptEnd)
              ? ` · ${fmtTime(excerptStart)}–${fmtTime(excerptEnd)}`
              : ''}
          </span>
          {doc.source_document_id && (
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => onShowViewer?.(doc.source_document_id, Number.isFinite(excerptStart) ? excerptStart : null)}
              title="Open the original recording at the point this excerpt starts"
            >
              Open the original{Number.isFinite(excerptStart) ? ` at ${fmtTime(excerptStart)}` : ''}
            </button>
          )}
        </div>
      )}
      {doc.derived_kind === 'redaction' && (
        <div className="inv-detail-derived" data-testid="redaction-provenance">
          <span>🖍️ Redacted copy — the original is untouched.</span>
          {doc.source_document_id && (
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => onShowViewer?.(doc.source_document_id)}
              title="Open the original recording"
            >
              Open the original
            </button>
          )}
        </div>
      )}
      {/* Title — the document's name everywhere; a person's title is kept through every re-run
          (plans/DOCUMENT_TITLES_PLAN.md). The file name stays as the second line. */}
      <div className="inv-detail-title" data-testid="doc-title-row" onClick={(e) => e.stopPropagation()}>
        <div className="inv-detail-label">Title</div>
        {renaming ? (
          <SegInlineEdit
            kind="rename" value={docTitle(doc)} busy={renameBusy}
            onCancel={() => setRenaming(false)}
            onSave={async ({ value }) => {
              setRenameBusy(true);
              try {
                const updated = await api.patch(`/documents/${doc.id}/title`, { title: value });
                setDocLocal({ ...doc, title: updated?.title ?? value, title_source: updated?.title_source ?? 'person' });
                setRenaming(false);
                setStatus({ text: `Now called “${String(updated?.title ?? value).slice(0, 60)}”`, error: false });
                onDocChange?.();
              } catch (err) {
                setStatus({ text: 'Could not rename: ' + (err?.message || 'unknown error'), error: true });
              } finally {
                setRenameBusy(false);
              }
            }}
          />
        ) : (
          <div className="inv-detail-title-line">
            <span className="inv-detail-title-text" title={doc.filename}>{docTitle(doc)}</span>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setRenaming(true)}
              title="Give this document the name you want to see everywhere — in the list, in Arc's answers, in exports">✎ Rename</button>
            {doc.title_source && TITLE_SOURCE_LABEL[doc.title_source] && (
              <span className="inv-detail-title-src">{TITLE_SOURCE_LABEL[doc.title_source]}</span>
            )}
            {hasOwnTitle(doc) && doc.filename && (
              <span className="inv-detail-title-file" title="The file as uploaded">file: {doc.filename}</span>
            )}
          </div>
        )}
      </div>

      {/* Metadata grid — human facts first; the UUID rides last as a
          copyable reference (it was pole position, which read as noise). */}
      <div className="inv-detail-grid">
        <div>
          <div className="inv-detail-label">Uploaded</div>
          <div className="inv-detail-value">{formatDate(uploadedAt(doc))}</div>
        </div>
        <div>
          <div className="inv-detail-label">Group</div>
          <div className="inv-detail-value">{doc.document_purpose_group || doc.document_category || '—'}</div>
        </div>
        <div>
          <div className="inv-detail-label">File Type</div>
          <div className="inv-detail-value">{(doc.file_type || '—').toUpperCase()}</div>
        </div>
        {(() => {
          const dur = getMediaDuration(doc);
          if (!dur) return null;
          const formatted = formatDuration(dur.seconds);
          if (!formatted) return null;
          return (
            <div>
              <div className="inv-detail-label">Duration</div>
              <div
                className="inv-detail-value"
                title={dur.isEstimated ? `Estimated (source: ${dur.source || 'unknown'})` : undefined}
              >
                {dur.isEstimated ? '~' + formatted : formatted}
              </div>
            </div>
          );
        })()}
        <div>
          <div className="inv-detail-label">Document ID</div>
          <button
            type="button"
            className="inv-detail-value inv-detail-mono btn-ghost"
            style={{ cursor: 'copy', padding: 0, border: 'none', textAlign: 'left' }}
            title="Click to copy the full document ID"
            onClick={() => navigator.clipboard?.writeText(doc.id).catch(() => {})}
          >
            {String(doc.id).slice(0, 8)}… ⧉
          </button>
        </div>
      </div>

      {/* Classification editor (collapsible) */}
      <div className="inv-detail-classification">
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => setShowClassification(!showClassification)}
        >
          {showClassification ? '▾ Hide classification' : '▸ Edit classification'}
          <span style={{ marginLeft: 8, fontSize: 11, color: '#8899aa' }}>
            {(doc.document_purpose || doc.document_type || '—')
              .replace(/_/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase())}
          </span>
        </button>
        {showClassification && (
          <div className="inv-classification-content">
            <ClassificationEditor doc={doc} onLocalChange={(updated) => {
              setDocLocal(prev => ({ ...prev, ...updated }));
              onDocChange?.();
            }} />
          </div>
        )}
      </div>

      {/* Document Readiness upgrade controls */}
      <div className="inv-detail-tier">
        <span className="inv-detail-label">Search readiness</span>
        <div className="inv-tier-controls">
          {(() => {
            // A compilation cut into documents is retired from retrieval: its
            // documents are what search reads. Offering it an upgrade would
            // be a button the backend refuses (container not indexable).
            if (doc?.is_compilation_container) {
              return (
                <span className="inv-tier-msg" data-testid="container-search-note">
                  Searched through the documents inside this file — each one has its own search readiness.
                </span>
              );
            }
            // Server-side in-flight signal — survives panel close/reopen.
            // Set by /upgrade-tier (queued) and transitioned by the bg
            // handler (processing → completed/failed). Disables the
            // button so a user navigating back doesn't see it as
            // clickable while the deep-extraction job is still running.
            //
            // Staleness escape: if the stamp is older than 15 min with no
            // started_at, OR started_at is older than 30 min, treat as
            // expired and re-enable the button. Bg handler retries
            // shouldn't exceed this. Without the escape, a one-time
            // failure that leaves "queued" or "processing" stamped
            // permanently locks the user out of retrying the upgrade.
            const upgradeJob = doc?.extraction?.tier_upgrade_job || null;
            const serverUpgradeStatus = upgradeJob?.status || null;
            const STALE_QUEUED_MS = 15 * 60 * 1000;
            const STALE_PROCESSING_MS = 30 * 60 * 1000;
            const isStaleStamp = (() => {
              if (!upgradeJob) return false;
              const now = Date.now();
              const queuedAt = upgradeJob.queued_at ? Date.parse(upgradeJob.queued_at) : null;
              const startedAt = upgradeJob.started_at ? Date.parse(upgradeJob.started_at) : null;
              if (serverUpgradeStatus === 'queued') {
                return queuedAt != null && (now - queuedAt) > STALE_QUEUED_MS;
              }
              if (serverUpgradeStatus === 'processing') {
                const ref = startedAt || queuedAt;
                return ref != null && (now - ref) > STALE_PROCESSING_MS;
              }
              return false;
            })();
            const serverInFlight = !isStaleStamp && (
              serverUpgradeStatus === 'queued'
              || serverUpgradeStatus === 'processing'
            );
            const deepInFlight = upgrading === 'deep' || serverInFlight;
            const surfaceInFlight = upgrading === 'surface';
            const anyInFlight = upgrading !== null || serverInFlight;
            return (
              <>
                {tier === 'excluded' && (
                  <span className="inv-tier-msg">
                    Not indexed — redacted copies aren't searchable.
                  </span>
                )}
                {tier === 'skim' && (
                  <>
                    <button
                      className="btn btn-sm btn-primary-ghost"
                      onClick={() => handleUpgrade('surface')}
                      disabled={anyInFlight || upgradeCosts === null}
                      title="Embed the sampled pages so Arc can quote them."
                    >
                      {surfaceInFlight
                        ? 'Upgrading…'
                        : `🔍 Surface search (${fmtCost(upgradeCosts?.surface)})`}
                    </button>
                    <button
                      className="btn btn-sm btn-primary-ghost"
                      onClick={() => handleUpgrade('deep')}
                      disabled={anyInFlight || upgradeCosts === null}
                      title="Extract every page so Arc can find anything."
                    >
                      {deepInFlight
                        ? 'Upgrading…'
                        : `🔎 Deep search (${fmtCost(upgradeCosts?.deep)})`}
                    </button>
                  </>
                )}
                {tier === 'surface' && (
                  <>
                    <span className="inv-tier-badge inv-tier--surface">✓ Surface search</span>
                    <button
                      className="btn btn-sm btn-primary-ghost"
                      onClick={() => handleUpgrade('deep')}
                      disabled={anyInFlight || upgradeCosts === null}
                      title="Extract every page so Arc can find anything."
                    >
                      {deepInFlight
                        ? 'Upgrading…'
                        : `🔎 Deep search (${fmtCost(upgradeCosts?.deep)})`}
                    </button>
                  </>
                )}
                {tier === 'deep' && (
                  <span className="inv-tier-badge inv-tier--deep">✓ Deep search</span>
                )}
              </>
            );
          })()}
        </div>
        {/* A refused/failed Deep search used to be silent: the worker stamps
            tier_upgrade_job.error (e.g. "This recording is 4 h 02 min long.
            Full transcription is limited to 4 h 00 min per file…") but
            nothing showed it. Say it, under the button that was clicked. */}
        {!upgradeMsg
          && doc?.extraction?.tier_upgrade_job?.status === 'failed'
          && doc?.extraction?.tier_upgrade_job?.error && (
          <span className="inv-tier-msg error" data-testid="tier-upgrade-failed">
            Last Deep search did not run: {doc.extraction.tier_upgrade_job.error}
          </span>
        )}
        {upgradeMsg && (
          <span className={`inv-tier-msg ${upgradeMsg.error ? 'error' : ''}`}>
            {upgradeMsg.text}
          </span>
        )}
      </div>

      {/* Action buttons — above the documents list so View / Move / Delete are in reach on a
          file that holds many documents (the list below can run to dozens of rows) */}
      <div className="inv-detail-actions">
        <button className="btn btn-sm" onClick={handleView}>👁️ View</button>
        <button className="btn btn-sm" onClick={() => setMoveOpen(!moveOpen)}>
          📁 Move
        </button>
        <button className="btn btn-sm" onClick={() => setCopyOpen(!copyOpen)}>
          📋 Copy to folder
        </button>
        <button
          className="btn btn-sm btn-danger-ghost"
          onClick={() => setDeleteConfirm(!deleteConfirm)}
        >
          🗑️ Delete
        </button>
        {isChecklist && (
          <span className="inv-role-badge inv-role-badge--active" title="This document is the active discovery checklist">
            ✓ Checklist Source
          </span>
        )}
        <button
          className="btn btn-sm btn-primary-ghost"
          onClick={() => setRoleModalOpen(true)}
        >
          {isChecklist ? '🔄 Re-import as Checklist' : '📋 Use as Checklist'}
        </button>
      </div>

      {/* Use as Checklist — import modal with live dry-run preview */}
      {roleModalOpen && (
        <ChecklistImportModal
          caseId={caseId}
          doc={doc}
          isChecklist={isChecklist}
          onClose={() => setRoleModalOpen(false)}
          onImported={handleChecklistImported}
        />
      )}

      {/* Move panel */}
      {moveOpen && (
        <div className="inv-move-panel">
          <select
            value={moveDest}
            onChange={e => setMoveDest(e.target.value)}
          >
            <option value="__unfiled__">— Unfiled —</option>
            {folderPaths.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
            <option value="__new__">+ New folder…</option>
          </select>
          {moveDest === '__new__' && (
            <input
              type="text"
              placeholder="/path/to/folder"
              value={newFolderPath}
              onChange={e => setNewFolderPath(e.target.value)}
            />
          )}
          <button className="btn btn-sm btn-primary" onClick={handleMove}>Move</button>
          <button className="btn btn-sm" onClick={() => setMoveOpen(false)}>Cancel</button>
        </div>
      )}

      {/* Copy panel — multi-folder membership */}
      {copyOpen && (
        <div className="inv-move-panel">
          {(doc.folder_paths || []).length > 0 && (
            <div className="inv-copy-current">
              Appears in:
              {(doc.folder_paths || []).map(p => (
                <span key={p} className="inv-copy-chip">
                  {p}{p === doc.folder_path ? ' (home)' : ''}
                  {p !== doc.folder_path && (
                    <button
                      type="button"
                      className="inv-copy-chip-remove"
                      title={`Remove from ${p}`}
                      onClick={() => handleRemoveCopy(p)}
                    >×</button>
                  )}
                </span>
              ))}
            </div>
          )}
          <select
            value={copyDest}
            onChange={e => setCopyDest(e.target.value)}
          >
            {folderPaths.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
            <option value="__new__">+ New folder…</option>
          </select>
          {copyDest === '__new__' && (
            <input
              type="text"
              placeholder="/path/to/folder"
              value={copyNewFolderPath}
              onChange={e => setCopyNewFolderPath(e.target.value)}
            />
          )}
          <button className="btn btn-sm btn-primary" onClick={handleCopy}>Copy</button>
          <button className="btn btn-sm" onClick={() => setCopyOpen(false)}>Cancel</button>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="inv-delete-confirm">
          <span>⚠️ Delete "{doc.filename}"? This cannot be undone.</span>
          <button className="btn btn-sm btn-danger" onClick={handleDelete}>
            Yes, delete
          </button>
          <button className="btn btn-sm" onClick={() => setDeleteConfirm(false)}>
            Cancel
          </button>
        </div>
      )}

      {/* Documents found inside this file (compilation PDFs) — Tier-C review, nothing is cut here */}
      {!isCutChild(doc) && <DetectedDocumentsSection doc={doc} onShowViewer={onShowViewer} />}

      {/* Summary */}
      {summary && (
        <div className="inv-detail-summary">{summary}</div>
      )}

      {/* Preview — manifest-driven dispatcher (PDF reader, image zoom/pan, sheet, keyframe filmstrip, …) */}
      <PreviewSection doc={doc} docId={doc.id} canonicalType={ct} />

      {/* Transcript toggle for audio/video */}
      {isMedia && (
        <div className="inv-detail-transcript">
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? '▾ Hide Transcript' : '▸ Show Transcript'}
            {transcriptData?.extraction_coverage != null && (
              <span style={{ marginLeft: 8, fontSize: 11, color: '#8899aa' }}>
                ({Math.round(transcriptData.extraction_coverage)}% transcribed)
              </span>
            )}
          </button>
          {showTranscript && (
            <div className="inv-transcript-content">
              {transcriptData === null && <span className="text-muted">Loading…</span>}
              {transcriptData && (
                <TranscriptBody
                  data={transcriptData}
                  onSeek={(t) => onShowViewer?.(doc.id, t)}
                  onCompleteFull={upgrade.handleFullTranscribe}
                  transcribing={upgrade.transcribing}
                  transcribeMsg={upgrade.transcribeMsg}
                  isPartial={isPartialTranscript}
                  isMedia={isMedia}
                  docId={doc.id}
                  tier={tier}
                  onRequestUpgrade={() => setUpgradeModalOpen(true)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Text excerpt for non-media docs */}
      {!isMedia && (
        <TextExcerptSection
          doc={doc}
          show={showTextExcerpt}
          onToggle={() => setShowTextExcerpt(!showTextExcerpt)}
          loaded={transcriptData}
        />
      )}

      {/* Entities */}
      <EntitiesSection
        doc={doc}
        show={showEntities}
        onToggle={() => setShowEntities(!showEntities)}
      />

      {/* Status */}
      {status && (
        <div className={`inv-detail-status ${status.error ? 'error' : ''}`}>
          {status.text}
        </div>
      )}

      {/* Upgrade-tier transcript consent modal */}
      <UpgradeTranscriptModal
        open={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        scope="document"
        targetId={doc.id}
        onComplete={() => {
          // Job is queued, not done — banner shows progress; the
          // dropdown's transcriptData polling (below) will auto-fetch
          // the new transcript when upgrade_job flips to completed.
          setStatus({ text: 'Transcript queued — track progress in the banner above.', error: false });
          setTranscriptData(null);  // re-fetch so upgrade_job.status="processing" is visible immediately
          onDocChange?.();
        }}
      />
    </div>
  );
}
